// How long the add-to-cart notification stays up before dismissing itself.
// Hovering pauses the countdown; pressing any key inside cancels it for that
// open, so nobody loses the panel mid-read or mid-tab. Set to 0 to disable.
const CART_NOTIFICATION_AUTO_DISMISS_MS = 4000;

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.autoDismissTimer = null;
    this.autoDismissCancelled = false;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.notification.addEventListener('mouseenter', () => this.pauseAutoDismiss());
    this.notification.addEventListener('mouseleave', () => this.startAutoDismiss());
    this.notification.addEventListener('keydown', () => this.cancelAutoDismiss());
  }

  startAutoDismiss() {
    if (!CART_NOTIFICATION_AUTO_DISMISS_MS || this.autoDismissCancelled) return;
    if (!this.notification.classList.contains('active')) return;

    this.pauseAutoDismiss();
    this.autoDismissTimer = setTimeout(() => this.close(), CART_NOTIFICATION_AUTO_DISMISS_MS);
  }

  pauseAutoDismiss() {
    if (!this.autoDismissTimer) return;

    clearTimeout(this.autoDismissTimer);
    this.autoDismissTimer = null;
  }

  cancelAutoDismiss() {
    this.autoDismissCancelled = true;
    this.pauseAutoDismiss();
  }

  open() {
    this.notification.classList.add('animate', 'active');

    // Fresh countdown each time, including when a second item is added while
    // the notification is still up.
    this.autoDismissCancelled = false;
    this.startAutoDismiss();

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.pauseAutoDismiss();
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target || !parsedState.sections[section.id]) return;

      target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        // Keeps the free shipping progress bar's total in step with the cart.
        id: 'ee-cart-shipping-bar',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
